import { useEffect, useMemo } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Lock, Save, Download, Copy } from "lucide-react";
import { MONTH_NAMES_PT, daysInMonth } from "./spiritual/constants";
import { useSpiritualSchedule } from "./spiritual/useSpiritualSchedule";
import { VinculoCard } from "./spiritual/VinculoCard";
import { DashboardPanel } from "./spiritual/DashboardPanel";
import { AlertsSection } from "./spiritual/AlertsSection";
import { EmailPrefsPanel } from "./spiritual/EmailPrefsPanel";
import { SharePanel } from "./spiritual/SharePanel";

export default function MemberSpiritual() {
  const s = useSpiritualSchedule();
  const dim = daysInMonth(s.year, s.month);
  const days = useMemo(() => Array.from({ length: dim }, (_, i) => i + 1), [dim]);

  // Feedback after clicking "Marcar como feito" in the alert email.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const marked = params.get("marked");
    if (!marked) return;
    if (marked === "done") toast.success("Prática marcada como feita. Bom trabalho!");
    else if (marked === "notfound") toast.error("Não foi possível encontrar a prática neste mês — marque manualmente.");
    else toast.error("Link inválido ou expirado.");
    window.history.replaceState({}, "", window.location.pathname);
  }, []);

  if (s.loading || !s.schedule) return <section className="container-page py-12"><p className="text-[hsl(var(--muted-foreground))]">A carregar…</p></section>;

  return (
    <section className="container-page py-10 lg:py-14" data-testid="member-spiritual-page">
      <div className="overline flex items-center gap-2"><Lock className="h-3 w-3" /> Estritamente privado</div>
      <h1 className="display-heading text-3xl sm:text-4xl mt-3">Horário Espiritual.</h1>
      <span className="accent-rule mt-5 block" />
      <p className="mt-4 max-w-2xl text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
        O seu compromisso de vida espiritual: práticas, marcação diária, exame particular e alertas. Apenas o sócio acede — pode optar por partilhar com o Assessor Espiritual.
      </p>

      {/* Month nav */}
      <div className="mt-8 flex items-center gap-3 flex-wrap" data-testid="spiritual-month-nav">
        <button onClick={s.prevMonth} className="h-10 w-10 inline-flex items-center justify-center border border-[hsl(var(--border))] hover:border-[hsl(var(--accent))]" data-testid="prev-month">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div className="display-heading text-2xl">{MONTH_NAMES_PT[s.month - 1]} {s.year}</div>
        <button onClick={s.nextMonth} className="h-10 w-10 inline-flex items-center justify-center border border-[hsl(var(--border))] hover:border-[hsl(var(--accent))]" data-testid="next-month">
          <ChevronRight className="h-4 w-4" />
        </button>
        <button onClick={s.copyPrevious}
          className="ml-auto inline-flex items-center gap-2 px-4 py-2 border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] text-xs uppercase tracking-[0.2em] hover:border-[hsl(var(--accent))] hover:text-[hsl(var(--accent))] transition-colors"
          title="Copia as práticas (título e frequência) do mês anterior para este mês"
          data-testid="spiritual-copy-previous">
          <Copy className="h-3.5 w-3.5" /> Copiar do mês anterior
        </button>
        <button onClick={s.downloadPdf}
          className="inline-flex items-center gap-2 px-4 py-2 border border-[hsl(var(--primary))] text-[hsl(var(--primary))] text-xs uppercase tracking-[0.2em] hover:bg-[hsl(var(--primary))] hover:text-white transition-colors"
          data-testid="spiritual-download-pdf">
          <Download className="h-3.5 w-3.5" /> Resumo PDF
        </button>
      </div>

      {/* Exame Particular */}
      <div className="mt-10 bg-white border border-[hsl(var(--border))] p-6 lg:p-8">
        <div className="overline">Exame particular do mês</div>
        <textarea value={s.exame} onChange={(e) => { s.setExame(e.target.value); s.setExameDirty(true); }} rows={4} maxLength={4000}
          placeholder="Escreva aqui o seu exame particular para este mês — propósito principal, áreas a trabalhar, etc."
          className="mt-4 w-full bg-white border border-[hsl(var(--border))] px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--accent))]"
          data-testid="spiritual-exame-input" />
        {s.exameDirty && (
          <button onClick={s.saveExame} className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-[hsl(var(--primary))] text-white text-xs uppercase tracking-[0.2em] hover:bg-[hsl(var(--primary))]/90" data-testid="spiritual-exame-save">
            <Save className="h-3.5 w-3.5" /> Guardar exame
          </button>
        )}
      </div>

      {/* Vínculos grid */}
      <div className="mt-10 space-y-10">
        {s.schedule.vinculos.map((v) => (
          <VinculoCard
            key={v.key}
            v={v} days={days} year={s.year} month={s.month}
            editTitle={s.editTitle} setEditTitle={s.setEditTitle}
            savePracticeTitle={s.savePracticeTitle} setFrequency={s.setFrequency}
            cycleCell={s.cycleCell} removePractice={s.removePractice} addPractice={s.addPractice}
          />
        ))}
      </div>

      <DashboardPanel dashboard={s.schedule.dashboard} month={s.month} />

      <AlertsSection alerts={s.alerts} reload={s.load} schedule={s.schedule} />

      <EmailPrefsPanel
        weeklyDigestEnabled={s.weeklyDigestEnabled}
        monthlyRecapEnabled={s.monthlyRecapEnabled}
        onToggleWeekly={s.toggleWeeklyDigest}
        onToggleMonthly={s.toggleMonthlyRecap}
      />

      <SharePanel assessors={s.assessors} sharedIds={s.sharedIds} toggleAssessor={s.toggleAssessor} />
    </section>
  );
}
